import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk'
import isNil from 'lodash/isNil'
import { PropsWithChildren, useEffect, useMemo } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { buildContext } from '../buildContext/buildContext'
import { createMeticulousInspectors } from '../inspector/meticulousInspector'

const LD_INITIALISATION_TIMEOUT_SECONDS = 5

const getLdConfiguration = () => {
  const clientId = process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID
  if (clientId === 'default') {
    // Local LD dev server — clientId 'default' is the ldcli project key
    // see https://launchdarkly.com/docs/guides/flags/ldcli-dev-server#configure-your-sdk-for-testing
    const localServerUrl = 'http://localhost:8765'
    return {
      clientId: 'default',
      options: {
        baseUrl: localServerUrl,
        streamUrl: localServerUrl,
        eventsUrl: localServerUrl,
        bootstrap: undefined,
      },
    }
  }

  if (!clientId) {
    throw new Error('No client id provided')
  }

  return {
    clientId,
    options: {
      bootstrap: 'localStorage' as const,
    },
  }
}

const LaunchDarklyContextUpdater = ({ children }: PropsWithChildren) => {
  const ldClient = useLDClient()
  const { org, user } = useAuthenticatedUser()
  // Re-identify when org or user changes (e.g., org switching)
  useEffect(() => {
    if (isNil(ldClient)) return

    const context = buildContext(org, user)
    void ldClient.identify(context)
  }, [ldClient, org, user])

  return <>{children}</>
}

export const LdFeatureFlagProvider = ({ children }: PropsWithChildren) => {
  const { org, user } = useAuthenticatedUser()
  const context = useMemo(() => buildContext(org, user), [org, user])
  const { options, clientId } = getLdConfiguration()

  return (
    <LDProvider
      clientSideID={clientId}
      context={context}
      options={{
        ...options,
        inspectors: createMeticulousInspectors(),
      }}
      reactOptions={{ useCamelCaseFlagKeys: false }}
      timeout={LD_INITIALISATION_TIMEOUT_SECONDS}
    >
      <LaunchDarklyContextUpdater>{children}</LaunchDarklyContextUpdater>
    </LDProvider>
  )
}
